import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { managerDashboardAPI, attendanceAPI } from '../../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar, Clock, Users, AlertTriangle, CheckCircle, MapPin, Monitor } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { toast } from 'sonner';

const TeamAttendancePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [attendance, setAttendance] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [auditData, setAuditData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });

  const loadTeamMembers = useCallback(async () => {
    try {
      const res = await managerDashboardAPI.getTeam();
      setTeamMembers(res.data);
    } catch (error) {
      console.error('Error loading team:', error);
    }
  }, []);

  useEffect(() => {
    loadTeamMembers();
  }, [loadTeamMembers]);

  const loadAttendance = useCallback(async () => {
    if (!user?.company_id) return;
    setLoading(true);
    try {
      const params = { date: selectedDate };
      if (selectedEmployee !== 'all') {
        params.employee_id = selectedEmployee;
      }
      const res = await attendanceAPI.getAll(user.company_id, params.date, params.employee_id);
      setAttendance(res.data);
    } catch (error) {
      console.error('Error loading attendance:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id, selectedDate, selectedEmployee]);

  useEffect(() => {
    loadAttendance();
  }, [loadAttendance]);

  const loadAuditData = async () => {
    setLoading(true);
    try {
      const res = await managerDashboardAPI.getTeamAttendanceAudit(dateRange.start, dateRange.end);
      setAuditData(res.data);
    } catch (error) {
      console.error('Error loading audit data:', error);
      toast.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'present':
        return <Badge className="bg-green-100 text-green-800">Present</Badge>;
      case 'late':
        return <Badge className="bg-amber-100 text-amber-800">Late</Badge>;
      case 'absent':
        return <Badge className="bg-red-100 text-red-800">Absent</Badge>;
      case 'on_leave':
        return <Badge className="bg-blue-100 text-blue-800">On Leave</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getEmployeeName = (employeeId) => {
    const member = teamMembers.find(m => m.id === employeeId);
    return member ? `${member.first_name} ${member.last_name}` : 'Unknown';
  };

  return (
    <div className="space-y-6" data-testid="team-attendance-page">
      <div>
        <h1 className="text-2xl font-bold">Team Attendance</h1>
        <p className="text-muted-foreground">View your direct reports&apos; attendance records</p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 flex-wrap">
            <div className="space-y-2">
              <label className="text-sm font-medium">Date</label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-[180px]"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Employee</label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Team Members</SelectItem>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Present</p>
                <p className="text-2xl font-bold">{attendance.filter(a => a.status === 'present').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Late</p>
                <p className="text-2xl font-bold">{attendance.filter(a => a.status === 'late').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Absent</p>
                <p className="text-2xl font-bold">{attendance.filter(a => a.status === 'absent').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total OT Hours</p>
                <p className="text-2xl font-bold">{(attendance.reduce((sum, a) => sum + (a.overtime_minutes || 0), 0) / 60).toFixed(1)}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Attendance Table */}
      <Card>
        <CardHeader>
          <CardTitle>Attendance Records</CardTitle>
          <CardDescription>Showing {attendance.length} records for {selectedDate}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Clock In</TableHead>
                <TableHead>Clock Out</TableHead>
                <TableHead>Worked Hours</TableHead>
                <TableHead>Late (min)</TableHead>
                <TableHead>OT (min)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attendance.map(record => (
                <TableRow key={record.id}>
                  <TableCell className="font-medium">{getEmployeeName(record.employee_id)}</TableCell>
                  <TableCell>{getStatusBadge(record.status)}</TableCell>
                  <TableCell>{record.clock_in ? format(new Date(record.clock_in), 'h:mm a') : '-'}</TableCell>
                  <TableCell>{record.clock_out ? format(new Date(record.clock_out), 'h:mm a') : '-'}</TableCell>
                  <TableCell>{record.worked_minutes ? (record.worked_minutes / 60).toFixed(1) : '-'}</TableCell>
                  <TableCell className={record.late_minutes > 0 ? 'text-amber-600' : ''}>{record.late_minutes || 0}</TableCell>
                  <TableCell className={record.overtime_minutes > 0 ? 'text-blue-600' : ''}>{record.overtime_minutes || 0}</TableCell>
                </TableRow>
              ))}
              {attendance.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No attendance records for this date
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* GPS/Device Audit Section */}
      <Card>
        <CardHeader>
          <CardTitle>Team Attendance Audit</CardTitle>
          <CardDescription>GPS and Device/IP tracking for your direct reports</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="space-y-2">
              <label className="text-sm font-medium">Start Date</label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-[180px]"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">End Date</label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-[180px]"
              />
            </div>
            <Button onClick={loadAuditData} disabled={loading}>
              Load Audit Data
            </Button>
          </div>

          {auditData && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              {/* GPS Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    GPS Tracking
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {auditData.gps_data?.map((item, idx) => (
                      <div key={idx} className="p-2 border rounded text-sm">
                        <div className="font-medium">{item.employee_name}</div>
                        <div className="text-muted-foreground">
                          {item.unique_locations_count} unique location(s)
                          {item.is_exception && (
                            <Badge variant="destructive" className="ml-2">Exception</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                    {(!auditData.gps_data || auditData.gps_data.length === 0) && (
                      <p className="text-muted-foreground text-sm">No GPS data available</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Device/IP Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Monitor className="h-4 w-4" />
                    Device/IP Tracking
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {auditData.device_ip_data?.map((item, idx) => (
                      <div key={idx} className="p-2 border rounded text-sm">
                        <div className="font-medium">{item.employee_name}</div>
                        <div className="text-muted-foreground">
                          {item.ip_count} IP(s), {item.device_count} Device(s)
                        </div>
                      </div>
                    ))}
                    {(!auditData.device_ip_data || auditData.device_ip_data.length === 0) && (
                      <p className="text-muted-foreground text-sm">No device data available</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamAttendancePage;
